import {CalibratedCropSetting, CalibrationResult, Calibrator, GrayImage} from "./types";

/**
 * Vertical field detection debug information.
 */
interface VInfo {
    /** X-axis column position used for sampling */
    xCol: number;
    /** Array indicating which pixels in the column are white */
    isWhiteCol: boolean[];
}

/**
 * Horizontal field detection result and debug information.
 */
interface HInfo {
    /** Detected horizontal field as [x, width] or null if not found */
    xw: [number, number] | null;
    /** Reason for detection failure if field was not found */
    reason?: string;
    /** Expanded left and right boundaries during detection */
    expandedLr?: [number, number];
}

/**
 * Configuration options for the calibration algorithm.
 */
export interface WinpharmaCalibrationOptions {
    /** White pixel threshold (0-255). Pixels >= this value are considered white */
    whiteThr?: number;
    /** Number of consecutive white pixels required at field edges */
    edgeWhitePx?: number;
    /** Minimum field height in pixels */
    minH?: number;
    /** Maximum field height in pixels */
    maxH?: number;
    /** Maximum allowed gap (non-white pixels) when expanding horizontally */
    maxGapRow?: number;
    /** Minimum field width in pixels */
    minW?: number;
    /** Maximum field width in pixels */
    maxW?: number;
    /** X-axis ratio for vertical sampling (0.0 to 1.0) */
    xRatio?: number;
}

/**
 * Web port of the Winpharma calibrator (detects lastname, firstname and NIR
 * fields by analyzing pixel patterns on the patient form screenshot).
 *
 * ⚠️ The 3 first detected white fields are mapped positionally on
 * [lastname, firstname, nir]: an unusual form layout shifts the mapping.
 */
export class WinpharmaCalibrator implements Calibrator {
    private options: Required<WinpharmaCalibrationOptions>;
    private slugs = ['lastname', 'firstname', 'nir'];
    private scaleFactor: number = 1.0;

    constructor(options: WinpharmaCalibrationOptions = {}) {
        this.options = {
            whiteThr: options.whiteThr ?? 252,
            edgeWhitePx: options.edgeWhitePx ?? 3,
            minH: options.minH ?? 20,
            maxH: options.maxH ?? 25,
            maxGapRow: options.maxGapRow ?? 20,
            minW: options.minW ?? 150,
            maxW: options.maxW ?? 750,
            xRatio: options.xRatio ?? 0.25,
        };
    }

    /**
     * Calibrate a screenshot and detect the fields.
     */
    calibrate(image: GrayImage, scaleFactor: number): CalibrationResult {
        const {data: gray, width, height} = image;
        this.scaleFactor = scaleFactor;

        if (!width || !height) {
            throw new Error('Unable to read image dimensions');
        }

        const {fields: yFields, debug: yDbg} = this.findYFields(gray, width, height);

        const topYFields = yFields.slice(0, 3);

        const horizontalInfo: HInfo[] = [];
        for (const [y, h] of topYFields) {
            const yc = y + Math.floor(h / 2);
            const result = this.findXField(gray, width, height, yc, yDbg.xCol);
            horizontalInfo.push({
                xw: result.xw as [number, number] | null,
                reason: result.debug.reason,
                expandedLr: result.debug.expandedLr
            });
        }

        const cropConfig: CalibratedCropSetting[] = [];

        for (let i = 0; i < topYFields.length; i++) {
            const [y, h] = topYFields[i];
            const xInfo = horizontalInfo[i];

            if (!xInfo.xw) {
                cropConfig.push({slug: this.slugs[i], x: null, y: y.toString(), width: null, height: h.toString()});
            } else {
                const [x, w] = xInfo.xw;
                cropConfig.push({
                    slug: this.slugs[i],
                    x: x.toString(),
                    y: y.toString(),
                    width: w.toString(),
                    height: h.toString()
                });
            }
        }

        return {
            config: cropConfig,
            debug: {
                xCol: yDbg.xCol,
                yFields: topYFields,
                horizontalInfo,
            },
        };
    }

    /**
     * Detects non-overlapping vertical fields.
     * Scans a vertical column at the specified x ratio to find field boundaries.
     */
    private findYFields(
        gray: Uint8Array,
        width: number,
        height: number
    ): { fields: [number, number][]; debug: VInfo } {
        const xCol = Math.floor(width * this.options.xRatio);
        const isWhite: boolean[] = [];

        for (let y = 0; y < height; y++) {
            const pixel = gray[y * width + xCol];
            isWhite.push(pixel >= this.options.whiteThr);
        }

        const fields: [number, number][] = [];
        let lastEnd = -1;
        let y = 50;

        while (y <= height - (this.options.minH * this.scaleFactor)) {
            if (y <= lastEnd) {
                y = lastEnd + 1;
                continue;
            }

            if (!this.isNonWhiteAt(y - 1, isWhite, height)) {
                y++;
                continue;
            }

            const edgeWhitePx = this.options.edgeWhitePx * this.scaleFactor;

            if (y + edgeWhitePx > height ||
                !isWhite.slice(y, y + edgeWhitePx).every(w => w)) {
                y++;
                continue;
            }

            let best: [number, number] | null = null;
            const endMin = y + (this.options.minH * this.scaleFactor) - 1;
            const endMax = Math.min(height - 1, y + (this.options.maxH * this.scaleFactor) - 1);

            for (let end = endMax; end >= endMin; end--) {
                if (end - edgeWhitePx + 1 < y) continue;
                if (!isWhite.slice(end - edgeWhitePx + 1, end + 1).every(w => w)) continue;
                if (!this.isNonWhiteAt(end + 1, isWhite, height)) continue;

                best = [y, end - y + 1];
                break;
            }

            if (best) {
                fields.push(best);
                lastEnd = best[0] + best[1] - 1;
                y = lastEnd + 1;
            } else {
                y++;
            }
        }

        return {
            fields,
            debug: {xCol, isWhiteCol: isWhite}
        };
    }

    /**
     * Detects the horizontal field on the center row.
     * Expands horizontally from the sample column to find field boundaries.
     */
    private findXField(
        gray: Uint8Array,
        width: number,
        height: number,
        yCenter: number,
        xCol: number
    ): { xw: [number, number] | null; debug: { reason?: string; expandedLr?: [number, number]; width?: number } } {
        if (yCenter < 0 || yCenter >= height) {
            return {xw: null, debug: {reason: 'y_center_oob'}};
        }

        const isWhite: boolean[] = [];

        for (let x = 0; x < width; x++) {
            const pixel = gray[yCenter * width + x];
            isWhite.push(pixel >= this.options.whiteThr - 15);
        }

        if (xCol < 0 || xCol >= width) {
            return {xw: null, debug: {reason: 'x_col_oob'}};
        }

        let left = this.expand(xCol, -1, isWhite, width);
        let right = this.expand(xCol, 1, isWhite, width);

        while (left < width && !isWhite[left]) left++;
        while (right >= 0 && !isWhite[right]) right--;

        if (right <= left) {
            return {xw: null, debug: {reason: 'no_white_run'}};
        }

        const edgeWhitePx = this.options.edgeWhitePx * this.scaleFactor;

        if (left + edgeWhitePx - 1 > right) {
            return {xw: null, debug: {reason: 'too_short'}};
        }

        if (!isWhite.slice(left, left + edgeWhitePx).every(w => w)) {
            return {xw: null, debug: {reason: 'no_white_start'}};
        }

        if (!isWhite.slice(right - edgeWhitePx + 1, right + 1).every(w => w)) {
            return {xw: null, debug: {reason: 'no_white_end'}};
        }

        const w = right - left + 1;
        if (w < (this.options.minW * this.scaleFactor) || w > (this.options.maxW * this.scaleFactor)) {
            return {xw: null, debug: {reason: `width_out_of_range (${w})`}};
        }

        return {
            xw: [left, w - (20 * this.scaleFactor)],
            debug: {expandedLr: [left, right], width: w}
        };
    }

    /**
     * Expands horizontally from a starting position to find field edges.
     * Allows gaps (non-white pixels) up to maxGapRow.
     */
    private expand(i: number, step: number, isWhite: boolean[], width: number): number {
        let gap = 0;
        while (i + step >= 0 && i + step < width) {
            const nextIndex = i + step;
            if (isWhite[nextIndex]) {
                gap = 0;
                i = nextIndex;
            } else {
                gap++;
                if (gap > (this.options.maxGapRow * this.scaleFactor)) {
                    break;
                }
                i = nextIndex;
            }
        }
        return i;
    }

    /**
     * Checks if the pixel at index i is non-white.
     * Treats out-of-bounds positions as boundaries (non-white).
     */
    private isNonWhiteAt(i: number, isWhite: boolean[], height: number): boolean {
        if (i < 0 || i >= height) return true;
        return !isWhite[i];
    }
}
